import React from 'react';
import AmortizationTable from './AmortizationTable';

const TraditionalLoanInterface = ({
  currentDebt,
  updateDebtData,
  updateCurrentDebt,
  formatCurrency,
  getCurrentLoanStatus,
  calculateMinimumPayment,
  generateFullSchedule,
  openCustomizeModal,
}) => {
  if (!currentDebt || currentDebt.type === 'credit-card') {
    return null;
  }

  // Calculate loan metrics with null safety
  const currentStatus = getCurrentLoanStatus() || {
    currentPaymentNumber: 1,
    currentBalance: currentDebt.data.principal || 0,
    totalPayments: currentDebt.data.term_months || 360,
    monthsRemaining: currentDebt.data.term_months || 360,
    originalPrincipal: currentDebt.data.principal || 0,
    paymentsMade: 0,
    remainingPayments: currentDebt.data.term_months || 360,
    totalInterestPaid: 0,
    projectedPayoffDate: 'N/A',
  };

  const { principal, annual_rate, term_months } = currentDebt.data;
  const monthlyRate = annual_rate / 12;

  // Use calculateMinimumPayment which respects custom minimum payment override
  const minimumPayment = calculateMinimumPayment ? calculateMinimumPayment(currentDebt) : 0;

  // Calculate base payment for display purposes (before custom override)
  const basePayment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, term_months))) /
    (Math.pow(1 + monthlyRate, term_months) - 1);
  const totalAdditionalCosts =
    (currentDebt.data.county_tax || 0) +
    (currentDebt.data.property_insurance || 0) +
    (currentDebt.data.pmi || 0) +
    (currentDebt.data.additional_monthly_costs || 0);

  // Use minimum payment (which includes custom override if set) plus any extra payments
  const totalMonthlyPayment =
    minimumPayment + (currentDebt.additionalPayments.recurring_extra || 0);

  // Loan Details Form (Column 1)
  const loanDetailsForm = React.createElement(
    'div',
    {
      key: 'loan-details',
      className: 'bg-card p-6 rounded-lg border border-border',
    },
    [
      React.createElement(
        'h2',
        {
          key: 'title',
          className: 'text-xl font-semibold mb-4 text-text-primary',
        },
        '🏠 Loan Details'
      ),

      React.createElement(
        'div',
        {
          key: 'form-container',
          className: 'space-y-4',
        },
        [
          // Debt Name with Customize Button
          React.createElement('div', { key: 'debt-name' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Debt Name'
            ),
            React.createElement(
              'div',
              {
                key: 'name-container',
                className: 'flex gap-2',
              },
              [
                React.createElement('input', {
                  key: 'name-input',
                  type: 'text',
                  value: currentDebt.data.name || '',
                  onChange: (e) => updateDebtData({ name: e.target.value }),
                  className:
                    'flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
                  placeholder: 'Enter debt name',
                }),
                React.createElement(
                  'button',
                  {
                    key: 'customize-button',
                    type: 'button',
                    onClick: () => openCustomizeModal(currentDebt),
                    className:
                      'px-3 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity flex items-center gap-2',
                    title: 'Customize name and emoji',
                  },
                  [
                    React.createElement('span', { key: 'emoji' }, currentDebt.customEmoji || '🏠'),
                    React.createElement('span', { key: 'text' }, '✏️'),
                  ]
                ),
              ]
            ),
          ]),

          // Principal Amount
          React.createElement('div', { key: 'principal' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Principal Amount ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.principal || '',
              onChange: (e) => updateDebtData({ principal: parseFloat(e.target.value) || 0 }),
              placeholder: '350000.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
          ]),

          // Annual Interest Rate
          React.createElement('div', { key: 'interest-rate' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Annual Interest Rate (%)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'text',
              value: currentDebt.data.interest_rate_display || '',
              onChange: (e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  const numericValue = parseFloat(value) || 0;
                  updateDebtData({
                    annual_rate: numericValue / 100,
                    interest_rate_display: value,
                  });
                }
              },
              placeholder: '6.5',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Enter as percentage (e.g., 6.5 for 6.5%)'
            ),
          ]),

          // Loan Term
          React.createElement('div', { key: 'term' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Loan Term (months)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              min: '1',
              value: currentDebt.data.term_months || '',
              onChange: (e) => updateDebtData({ term_months: parseInt(e.target.value) || 0 }),
              placeholder: '360',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              '360 months = 30 years'
            ),
          ]),

          // First Payment Date
          React.createElement('div', { key: 'first-payment' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'First Payment Date'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'date',
              value: currentDebt.data.first_payment_date || '',
              onChange: (e) => updateDebtData({ first_payment_date: e.target.value }),
              className:
                'w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Used to calculate current balance'
            ),
          ]),
        ]
      ),
    ]
  );

  // Additional Costs (Column 2)
  const additionalCosts = React.createElement(
    'div',
    {
      key: 'additional-costs',
      className: 'bg-card p-6 rounded-lg border border-border',
    },
    [
      React.createElement(
        'h2',
        {
          key: 'title',
          className: 'text-xl font-semibold mb-4 text-text-primary',
        },
        '💰 Additional Monthly Costs'
      ),

      React.createElement(
        'div',
        {
          key: 'costs-container',
          className: 'space-y-4',
        },
        [
          // County Tax
          React.createElement('div', { key: 'county-tax' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'County Tax ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.county_tax || '',
              onChange: (e) => updateDebtData({ county_tax: parseFloat(e.target.value) || 0 }),
              placeholder: '208.33',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
          ]),

          // Property Insurance
          React.createElement('div', { key: 'property-insurance' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Property Insurance ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.property_insurance || '',
              onChange: (e) =>
                updateDebtData({ property_insurance: parseFloat(e.target.value) || 0 }),
              placeholder: '125.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
          ]),

          // PMI
          React.createElement('div', { key: 'pmi' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Mortgage Insurance (PMI) ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.pmi || '',
              onChange: (e) => updateDebtData({ pmi: parseFloat(e.target.value) || 0 }),
              placeholder: '104.17',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
          ]),

          // Additional Monthly Costs
          React.createElement('div', { key: 'additional-costs' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Additional Monthly Costs ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.additional_monthly_costs || '',
              onChange: (e) =>
                updateDebtData({ additional_monthly_costs: parseFloat(e.target.value) || 0 }),
              placeholder: '0.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'HOA fees, maintenance reserves, etc.'
            ),
          ]),

          // Recurring Extra Principal
          React.createElement('div', { key: 'recurring-extra' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Recurring Extra Principal ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.additionalPayments.recurring_extra || '',
              onChange: (e) =>
                updateCurrentDebt({
                  additionalPayments: {
                    ...currentDebt.additionalPayments,
                    recurring_extra: parseFloat(e.target.value) || 0,
                  },
                }),
              placeholder: '0.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Extra amount applied to principal each month'
            ),
          ]),

          // Custom Minimum Payment Override
          React.createElement('div', { key: 'custom-min-payment' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Custom Minimum Payment Override ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.custom_minimum_payment || '',
              onChange: (e) =>
                updateDebtData({ custom_minimum_payment: parseFloat(e.target.value) || null }),
              placeholder: 'Leave blank to use calculated payment',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              '💡 Use this if you know the exact minimum payment amount'
            ),
          ]),

          // Total Additional Costs Summary
          React.createElement(
            'div',
            {
              key: 'total-summary',
              className: 'mt-4 p-4 bg-primary/10 rounded-lg border border-primary',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'label',
                  className: 'text-sm font-medium text-primary mb-2',
                },
                'Total Additional Costs:'
              ),
              React.createElement(
                'div',
                {
                  key: 'amount',
                  className: 'text-2xl font-bold text-text-primary',
                },
                '$' + formatCurrency(totalAdditionalCosts)
              ),
            ]
          ),
        ]
      ),
    ]
  );

  // Current Loan Status (Column 3)
  const currentLoanStatus = React.createElement(
    'div',
    {
      key: 'current-status',
      className: 'bg-card p-6 rounded-lg border border-border',
      id: 'current-status-print',
    },
    [
      React.createElement(
        'div',
        {
          key: 'header',
          className: 'flex justify-between items-center mb-4',
        },
        [
          React.createElement(
            'h2',
            {
              key: 'title',
              className: 'text-xl font-semibold text-text-primary',
            },
            '📊 Current Loan Status'
          ),
        ]
      ),

      React.createElement(
        'div',
        {
          key: 'status-content',
          className: 'space-y-4',
        },
        [
          // Current Status Overview
          React.createElement(
            'div',
            {
              key: 'status-overview',
              className: 'bg-surface-muted border border-border p-4 rounded-lg',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'date-header',
                  className: 'flex items-center gap-2 mb-4',
                },
                [
                  React.createElement('span', { key: 'icon', className: 'text-2xl' }, '📅'),
                  React.createElement(
                    'h3',
                    {
                      key: 'title',
                      className: 'font-semibold text-text-primary',
                    },
                    'As of Today (' + new Date().toLocaleDateString() + ')'
                  ),
                ]
              ),

              React.createElement(
                'div',
                {
                  key: 'status-boxes',
                  className: 'space-y-2',
                },
                [
                  React.createElement(
                    'div',
                    {
                      key: 'balance-box',
                      className: 'bg-card py-2 px-3 rounded border border-border',
                    },
                    [
                      React.createElement(
                        'div',
                        { key: 'label', className: 'text-text-muted text-xs font-medium' },
                        'Current Balance'
                      ),
                      React.createElement(
                        'div',
                        { key: 'value', className: 'font-bold text-lg text-primary' },
                        '$' + formatCurrency(currentStatus.currentBalance)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'payments-box',
                      className: 'bg-card py-2 px-3 rounded border border-border',
                    },
                    [
                      React.createElement(
                        'div',
                        { key: 'label', className: 'text-text-muted text-xs font-medium' },
                        'Payments Made'
                      ),
                      React.createElement(
                        'div',
                        { key: 'value', className: 'font-bold text-lg text-text-primary' },
                        currentStatus.paymentsMade + ' of ' + term_months
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'interest-box',
                      className: 'bg-card py-2 px-3 rounded border border-border',
                    },
                    [
                      React.createElement(
                        'div',
                        { key: 'label', className: 'text-text-muted text-xs font-medium' },
                        'Interest Paid'
                      ),
                      React.createElement(
                        'div',
                        { key: 'value', className: 'font-bold text-lg text-destructive' },
                        '$' + formatCurrency(currentStatus.totalInterestPaid)
                      ),
                    ]
                  ),
                ]
              ),
            ]
          ),

          // Monthly Payment Breakdown
          React.createElement(
            'div',
            {
              key: 'payment-breakdown',
              className: 'bg-card p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-3',
                },
                'Monthly Payment Breakdown:'
              ),
              React.createElement(
                'div',
                {
                  key: 'breakdown-items',
                  className: 'space-y-2 text-sm text-text-primary',
                },
                [
                  // Show custom minimum payment override if set, otherwise show calculated breakdown
                  currentDebt.data.custom_minimum_payment
                    ? React.createElement(
                        'div',
                        {
                          key: 'custom-payment',
                          className: 'flex justify-between bg-primary/10 -mx-2 px-2 py-1 rounded',
                        },
                        [
                          React.createElement(
                            'span',
                            { key: 'label', className: 'text-primary font-medium' },
                            '🎛️ Custom Minimum Payment:'
                          ),
                          React.createElement(
                            'span',
                            { key: 'value', className: 'font-bold text-primary' },
                            '$' + formatCurrency(currentDebt.data.custom_minimum_payment)
                          ),
                        ]
                      )
                    : React.createElement(
                        'div',
                        {
                          key: 'principal-interest',
                          className: 'flex justify-between',
                        },
                        [
                          React.createElement(
                            'span',
                            { key: 'label', className: 'text-text-primary' },
                            'Principal & Interest:'
                          ),
                          React.createElement(
                            'span',
                            { key: 'value', className: 'font-medium text-text-primary' },
                            '$' + formatCurrency(basePayment)
                          ),
                        ]
                      ),

                  // Only show additional cost breakdown if not using custom minimum payment
                  !currentDebt.data.custom_minimum_payment &&
                    React.createElement(
                      'div',
                      {
                        key: 'county-tax',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement(
                          'span',
                          { key: 'label', className: 'text-text-primary' },
                          'County Tax:'
                        ),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-text-primary' },
                          '$' + formatCurrency(currentDebt.data.county_tax || 0)
                        ),
                      ]
                    ),
                  !currentDebt.data.custom_minimum_payment &&
                    React.createElement(
                      'div',
                      {
                        key: 'property-insurance',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement(
                          'span',
                          { key: 'label', className: 'text-text-primary' },
                          'Property Insurance:'
                        ),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-text-primary' },
                          '$' + formatCurrency(currentDebt.data.property_insurance || 0)
                        ),
                      ]
                    ),
                  !currentDebt.data.custom_minimum_payment &&
                    React.createElement(
                      'div',
                      {
                        key: 'pmi',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement(
                          'span',
                          { key: 'label', className: 'text-text-primary' },
                          'PMI:'
                        ),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-text-primary' },
                          '$' + formatCurrency(currentDebt.data.pmi || 0)
                        ),
                      ]
                    ),
                  !currentDebt.data.custom_minimum_payment &&
                    currentDebt.data.additional_monthly_costs > 0 &&
                    React.createElement(
                      'div',
                      {
                        key: 'additional-costs',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement(
                          'span',
                          { key: 'label', className: 'text-text-primary' },
                          'Additional Monthly Costs:'
                        ),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-text-primary' },
                          '$' + formatCurrency(currentDebt.data.additional_monthly_costs)
                        ),
                      ]
                    ),
                  currentDebt.additionalPayments.recurring_extra > 0 &&
                    React.createElement(
                      'div',
                      {
                        key: 'recurring-extra',
                        className: 'flex justify-between text-primary',
                      },
                      [
                        React.createElement(
                          'span',
                          { key: 'label', className: 'text-text-primary' },
                          'Recurring Extra Principal:'
                        ),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-primary' },
                          '+$' + formatCurrency(currentDebt.additionalPayments.recurring_extra)
                        ),
                      ]
                    ),
                  React.createElement(
                    'div',
                    {
                      key: 'total-payment',
                      className:
                        'border-t border-border pt-2 flex justify-between font-bold text-lg text-text-primary',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'text-text-primary' },
                        'Total Monthly Payment:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'text-primary' },
                        '$' + formatCurrency(totalMonthlyPayment)
                      ),
                    ]
                  ),
                ]
              ),
            ]
          ),

          // Remaining Loan Details
          React.createElement(
            'div',
            {
              key: 'remaining-details',
              className: 'bg-card p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-3',
                },
                'Remaining Loan Details:'
              ),
              React.createElement(
                'div',
                {
                  key: 'details-items',
                  className: 'space-y-2 text-sm text-text-primary',
                },
                [
                  React.createElement(
                    'div',
                    {
                      key: 'original-principal',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'text-text-primary' },
                        'Original Principal:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium text-text-primary' },
                        '$' + formatCurrency(currentStatus.originalPrincipal)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'remaining-balance',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'text-text-primary' },
                        'Remaining Balance:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium text-primary' },
                        '$' + formatCurrency(currentStatus.currentBalance)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'remaining-payments',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'text-text-primary' },
                        'Remaining Payments:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium text-text-primary' },
                        currentStatus.remainingPayments + ' payments'
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'payoff-date',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'text-text-primary' },
                        'Projected Payoff Date:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium text-text-primary' },
                        currentStatus.projectedPayoffDate
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'next-payment',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'text-text-primary' },
                        'Next Payment Date:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium text-text-primary' },
                        (() => {
                          const nextPayment =
                            currentStatus.fullSchedule && currentStatus.fullSchedule.find
                              ? currentStatus.fullSchedule.find((p) => p.is_current_payment)
                              : null;
                          return nextPayment ? nextPayment.date : 'N/A';
                        })()
                      ),
                    ]
                  ),
                ]
              ),
            ]
          ),
        ]
      ),
    ]
  );

  // Main container with 3-column grid and amortization table
  return React.createElement(
    'div',
    {
      className: 'space-y-8',
    },
    [
      // Original 3-column grid
      React.createElement(
        'div',
        {
          key: 'loan-details-grid',
          className: 'grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8',
        },
        [loanDetailsForm, additionalCosts, currentLoanStatus]
      ),

      // Interactive Amortization Table
      React.createElement(AmortizationTable, {
        key: 'amortization-table',
        currentDebt: currentDebt,
        updateCurrentDebt: updateCurrentDebt,
        formatCurrency: formatCurrency,
        generateFullSchedule: generateFullSchedule,
      }),
    ]
  );
};

export default TraditionalLoanInterface;
